import { Input, Stack } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import type { ChangeEvent, ComponentPropsWithoutRef, MutableRefObject, ReactElement } from 'react';
import { forwardRef, useCallback, useMemo } from 'react';
import type { RegisterOptions } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { useControllerWithWarnings } from '../../../custom-hooks/useControllerWithWarnings';

const capitalize = (value: string) =>
  value[0] ? value[0].toLocaleUpperCase() + value.slice(1) : value;

interface FormInputProps extends ComponentPropsWithoutRef<'input'> {
  className?: string;
  description?: string;
  label?: string;
  name: string;
  placeholder?: string;
  isRequired?: boolean;
  labelRef?: MutableRefObject<HTMLLabelElement | null>;
  validations?: RegisterOptions;
  warningValidation?: (value: string) => boolean | string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  normalize?: (value: string) => any;
  format?: (value: any) => string;
  shouldForceCapitalize?: boolean;
  valueOverride?: string;
  iconSuffix?: ReactElement;
}

const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      className,
      description,
      label,
      name,
      placeholder,
      isRequired = false,
      labelRef,
      normalize,
      format,
      validations,
      onChange,
      shouldForceCapitalize = false,
      warningValidation,
      valueOverride,
      iconSuffix,
      ...rest
    },
    ref,
  ) => {
    const { t, nonDebugT } = useTranslation();
    const { control } = useFormContext();
    const {
      field,
      fieldState: { error, warning },
    } = useControllerWithWarnings({ name, control, rules: validations, warningValidation });

    const composeRefs = (node: HTMLInputElement) => {
      field.ref(node);

      const localRef = ref;
      if (localRef && 'current' in localRef) {
        localRef.current = node;
      }
    };

    const labelId = `${name}-label`;
    const errorId = `${name}-error`;
    const descriptionId = `${name}-description`;
    const resolvedLabel = useMemo(
      () => (isRequired ? nonDebugT('Input.Field.Required', label) : label),
      [isRequired, label, nonDebugT],
    );

    const warningMessage = useMemo(() => (warning ? t(warning) : undefined), [warning, t]);

    const handleOnChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        let normalizedValue = e.target.value;
        if (shouldForceCapitalize) {
          normalizedValue = capitalize(normalizedValue);
        }
        if (normalize) {
          normalizedValue = normalize(normalizedValue);
        }

        field.onChange(normalizedValue);
        onChange?.(e);
      },
      [field, normalize, onChange, shouldForceCapitalize],
    );

    const hasError = Boolean(error?.message);
    const formattedValue = format ? format(field.value) : field.value;
    const value = typeof valueOverride !== 'undefined' ? valueOverride : formattedValue;

    return (
      <Stack className={className} spacing={4}>
        <Input.Label htmlFor={name} id={labelId} ref={labelRef}>
          {resolvedLabel}
        </Input.Label>
        <Input
          {...field}
          aria-labelledby={`${labelId} ${errorId}`}
          data-testid={`${name}-input`}
          descriptionId={descriptionId}
          errorId={errorId}
          hasError={hasError}
          hasWarning={Boolean(warningMessage)}
          iconSuffix={iconSuffix}
          id={name}
          onBlur={field.onBlur}
          onChange={handleOnChange}
          placeholder={placeholder}
          ref={composeRefs}
          required={isRequired}
          value={value}
          {...rest}
        />
        <Input.Meta
          description={description}
          descriptionId={descriptionId}
          error={error?.message}
          errorId={errorId}
          hasError={hasError}
          hasWarning={Boolean(warningMessage)}
          warning={warningMessage}
        />
      </Stack>
    );
  },
);

export default FormInput;
